/**
 * Super Admin Layout
 *
 * Main layout wrapper for the Super Admin Portal with a "Command Center" aesthetic.
 * Features a collapsible dark sidebar, top navigation with environment badge,
 * and role-based access control.
 */
import { useEffect } from 'react';
import { Outlet, Link, useRouterState, useNavigate } from '@tanstack/react-router';
import {
  SidebarProvider,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarItem,
  SidebarToggle,
  TopNav,
  useSidebar,
} from '@respectly/ui';
import {
  LayoutDashboard,
  Building2,
  Inbox,
  Megaphone,
  CreditCard,
  Layers,
  ScrollText,
  Server,
  Settings,
  Zap,
  BookOpen,
  BarChart3,
  Phone,
  Sparkles,
  BrainCircuit,
  MessageSquare,
  MessagesSquare,
  Users,
} from 'lucide-react';
import { useAuth, ProtectedRoute } from '@respectly/utils/auth';
import { EnvironmentBadge } from '../components/EnvironmentBadge';
import { useChatQueueStore } from '../stores/useChatQueueStore';
import { useChatQueueStream } from '../hooks/useChatQueueStream';
import { primeAudio } from '../lib/notificationSound';

/**
 * The waiting-chat count, on the nav item rather than in a bell.
 *
 * `SidebarItem` takes an icon element and has no badge slot, and adding one to
 * the shared component for a single caller would be the wrong trade. The count
 * rides inside the icon instead, which also puts it where it stays visible when
 * the sidebar is collapsed to icons only — the state in which someone is least
 * likely to notice a queue building up.
 */
function LiveChatsIcon() {
  const waiting = useChatQueueStore((s) => s.waiting);
  return (
    <span className="relative inline-flex">
      <MessagesSquare className="h-5 w-5" />
      {waiting > 0 && (
        <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold leading-none text-white">
          {waiting > 9 ? '9+' : waiting}
        </span>
      )}
    </span>
  );
}

// Navigation configuration grouped by section
const navConfig = {
  platform: [
    { icon: <LayoutDashboard className="h-5 w-5" />, label: 'Dashboard', href: '/dashboard' },
    { icon: <Building2 className="h-5 w-5" />, label: 'Accounts & Users', href: '/accounts' },
    { icon: <BarChart3 className="h-5 w-5" />, label: 'Usage Analytics', href: '/usage' },
  ],
  operations: [
    { icon: <Inbox className="h-5 w-5" />, label: 'Support Inbox', href: '/support' },
    { icon: <Megaphone className="h-5 w-5" />, label: 'Campaigns', href: '/campaigns' },
    { icon: <BookOpen className="h-5 w-5" />, label: 'Prompt Templates', href: '/settings/prompt-templates' },
    // { icon: <LayoutTemplate className="h-5 w-5" />, label: 'Document & Form Templates', href: '/templates/doc-and-form' },
    { icon: <Layers className="h-5 w-5" />, label: 'Plan Setup', href: '/plan-setup' },
    { icon: <CreditCard className="h-5 w-5" />, label: 'Plans & Billing', href: '/billing' },
    { icon: <Phone className="h-5 w-5" />, label: 'Number Inventory', href: '/inventory' },
    { icon: <ScrollText className="h-5 w-5" />, label: 'Audit Logs', href: '/audit' },
    { icon: <Server className="h-5 w-5" />, label: 'Platform Operations', href: '/operations' },
    { icon: <Sparkles className="h-5 w-5" />, label: 'AI Console', href: '/ai-console' },
    { icon: <BrainCircuit className="h-5 w-5" />, label: 'AI Operations', href: '/ai-operations' },
    { icon: <MessageSquare className="h-5 w-5" />, label: 'Chat Widgets', href: '/chat-widgets' },
    { icon: <LiveChatsIcon />, label: 'Live Chats', href: '/live-chats' },
    { icon: <Users className="h-5 w-5" />, label: 'Chat Leads', href: '/chat-leads' },
  ],
  configuration: [
    { icon: <Settings className="h-5 w-5" />, label: 'Settings', href: '/settings' },
  ],
};

// Sidebar header with branding
function SidebarBranding() {
  const { isCollapsed } = useSidebar();

  return (
    <Link to="/dashboard" className="flex items-center gap-3 group">
      {isCollapsed ? (
        <img src="/favicon.png" alt="Respectly" className="h-9 w-9 shrink-0 rounded-full" />
      ) : (
        <img src="/logo-white.svg" alt="Respectly" className="h-9 w-auto" />
      )}
    </Link>
  );
}

// Quick status indicator in sidebar footer
function PlatformStatus() {
  const { isCollapsed } = useSidebar();

  return (
    <div className={`flex items-center gap-2 mb-3 px-1 ${isCollapsed ? 'justify-center' : ''}`}>
      <div className="flex items-center gap-1.5">
        <Zap className="h-3.5 w-3.5 text-emerald-400" />
        {!isCollapsed && (
          <span className="text-[11px] font-medium text-emerald-400">
            All Systems Operational
          </span>
        )}
      </div>
    </div>
  );
}

// Helper to determine if path is active
function isPathActive(href: string, currentPath: string): boolean {
  if (href === '/dashboard') {
    return currentPath === '/dashboard' || currentPath === '/';
  }
  return currentPath.startsWith(href);
}

export function SuperAdminLayout() {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Build user object from auth context with role display
  const currentUser = user
    ? {
        name: `${user.firstName} ${user.lastName}`.trim() || 'Admin',
        email: user.email,
        role: formatRoleDisplay(user.role),
      }
    : {
        name: 'Guest',
        email: '',
        role: '',
      };

  const handleLogout = async () => {
    try {
      await logout();
      navigate({ to: '/login' });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSettings = () => {
    navigate({ to: '/settings' });
  };

  // Held open for the whole portal, not just the Live Chats page: the point of a
  // waiting count is to reach someone who is looking at something else.
  useChatQueueStream();

  /**
   * Buy the right to make a sound, from the first click or keypress.
   *
   * An `AudioContext` is created suspended and only a user gesture can resume it,
   * so the chime for a new visitor would be silently dropped if we waited until
   * there was something to play. Doing it here means the permission is already in
   * hand by the time it matters — anyone logged in has clicked something.
   *
   * Listeners are `once` and passive, and they unregister themselves.
   */
  useEffect(() => {
    const prime = (): void => void primeAudio();
    const options = { once: true, passive: true } as const;
    window.addEventListener('pointerdown', prime, options);
    window.addEventListener('keydown', prime, options);
    return () => {
      window.removeEventListener('pointerdown', prime);
      window.removeEventListener('keydown', prime);
    };
  }, []);

  return (
    <ProtectedRoute requiredRoles={['SuperAdmin', 'SupportAdmin', 'BillingAdmin', 'ComplianceAdmin', 'ReadOnlyAdmin']}>
      <SidebarProvider storageKey="super-admin-sidebar-collapsed">
        <div className="flex min-h-screen bg-slate-100 dark:bg-slate-950">
          {/* Sidebar */}
          <Sidebar className="shrink-0">
            <SidebarHeader>
              <SidebarBranding />
            </SidebarHeader>

            <SidebarContent>
              {/* Platform Section */}
              <SidebarGroup>
                <SidebarGroupLabel>Platform</SidebarGroupLabel>
                {navConfig.platform.map((item) => (
                  <SidebarItem
                    key={item.href}
                    icon={item.icon}
                    label={item.label}
                    href={item.href}
                    active={isPathActive(item.href, currentPath)}
                    as={Link}
                  />
                ))}
              </SidebarGroup>

              {/* Operations Section */}
              <SidebarGroup>
                <SidebarGroupLabel>Operations</SidebarGroupLabel>
                {navConfig.operations.map((item) => (
                  <SidebarItem
                    key={item.href}
                    icon={item.icon}
                    label={item.label}
                    href={item.href}
                    active={isPathActive(item.href, currentPath)}
                    as={Link}
                  />
                ))}
              </SidebarGroup>

              {/* Configuration Section */}
              <SidebarGroup>
                <SidebarGroupLabel>Configuration</SidebarGroupLabel>
                {navConfig.configuration.map((item) => (
                  <SidebarItem
                    key={item.href}
                    icon={item.icon}
                    label={item.label}
                    href={item.href}
                    active={isPathActive(item.href, currentPath)}
                    as={Link}
                  />
                ))}
              </SidebarGroup>
            </SidebarContent>

            <SidebarFooter>
              <PlatformStatus />
              <SidebarToggle />
            </SidebarFooter>
          </Sidebar>

          {/* Main content area */}
          <div className="flex flex-1 flex-col min-w-0">
            <TopNav
              user={currentUser}
              onLogout={handleLogout}
              onSettings={handleSettings}
              onProfile={() => navigate({ to: '/settings' })}
              actions={<EnvironmentBadge />}
              searchEnabled={false}
            />

            {/* Main content */}
            <main className="flex-1 overflow-auto p-4 lg:p-6">
              <Outlet />
            </main>
          </div>
        </div>
      </SidebarProvider>
    </ProtectedRoute>
  );
}

// Format role for display
function formatRoleDisplay(role: string): string {
  const roleMap: Record<string, string> = {
    SuperAdmin: 'Super Admin',
    SupportAdmin: 'Support Admin',
    BillingAdmin: 'Billing Admin',
    ComplianceAdmin: 'Compliance Admin',
    ReadOnlyAdmin: 'Read-Only Admin',
    // Fallback for tenant user roles
    UserTenant: 'User Tenant',
    TeamUser: 'Team User',
  };
  return roleMap[role] || role;
}

export default SuperAdminLayout;
